<script setup lang="ts">
import { CourseContentActivityType, CourseType } from "~/enums/course.enums";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { onBeforeUnmount, watch, ref, computed } from "vue";

const emit = defineEmits<{
  (e: 'rewatch'): void;
}>();

const { autoAdvance } = useUserPreferences();
const { course, getNextActivity } = useCourseState();
const route = useRoute();
const router = useRouter();

const nextActivity = computed(() => {
  const currentActivityId = route.params.quizId || route.params.lessonId;
  return getNextActivity(currentActivityId as string);
});

const nextActivityUrl = computed(() => {
  const next = nextActivity.value;
  if (!next) {
    return null;
  }
  return `/courses/${route.params.courseId}/${next.type === CourseContentActivityType.Lesson ? 'lessons' : 'quizzes'}/${next.id}`;
});

const autoAdvanceTimeout = ref<ReturnType<typeof setTimeout>>();

const onRewatch = () => {
  clearTimeout(autoAdvanceTimeout.value);
  emit('rewatch');
};

const onNext = () => {
  clearTimeout(autoAdvanceTimeout.value);

  if (nextActivityUrl.value) {
    router.push(nextActivityUrl.value);
  } else {
    router.push(`/courses/${route.params.courseId}`);
  }
};

onBeforeMount(() => {
  if (autoAdvance.value) {
    autoAdvanceTimeout.value = setTimeout(onNext, 8000);
  }
});

watch(route, () => {
  clearTimeout(autoAdvanceTimeout.value);
});

onBeforeUnmount(() => {
  clearTimeout(autoAdvanceTimeout.value);
});
</script>

<template>
  <div class="flex items-center aspect-video justify-center bg-neutral-100 lg:rounded-2xl">
    <div class="flex flex-col justify-between gap-4 p-6 w-full h-full bg-neutral-5 lg:rounded-2xl lg:max-w-[25rem] lg:max-h-[15rem]">
      <div>
        <div class="text-14 uppercase tracking-widest text-neutral-80 font-bold mb-4">
          {{nextActivity ? 'Up Next' : 'Congratulations!'}}
        </div>

        <div class="text-20 font-bold text-neutral-100">
          <template v-if="nextActivity">
            {{nextActivity?.title}}
          </template>

          <template v-else>
            You've reached the last {{course?.type === CourseType.Course ? 'lesson' : 'case'}} in this {{course?.type}}.
          </template>
        </div>
      </div>

      <div class="flex items-center justify-end gap-2">
        <Button
          text
          severity="secondary"
          @click="onRewatch"
        >
          <div class="flex items-center gap-2 text-16 font-bold z-[1]">
            <FontAwesomeIcon icon="fa-arrow-rotate-left" />
            <div>Rewatch</div>
          </div>
        </Button>

        <NuxtLink :to="nextActivityUrl ?? `/courses/${route.params.courseId}`">
          <Button
            class="relative overflow-clip border-none px-8 bg-neutral-20 text-neutral-90"
            :href="nextActivityUrl"
            @click="onNext()"
          >
            <div
              class="bg-blue-30 w-0 h-full absolute top-0 left-0 transition-all"
              :class="{'animate-fillHorizontal': nextActivityUrl && autoAdvance}"
            />

            <div class="flex items-center gap-2 text-16 font-bold z-[1]">
              <div>
                {{nextActivityUrl ? 'Next' : 'Continue'}}
              </div>
              <FontAwesomeIcon icon="fa-forward" />
            </div>
          </Button>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>